import { useProPilotDockActions, useProPilotDockElement } from "PFApp/stores/propilot_dock_store";
import { useProPilotLifecycleActions, useProPilotStatus } from "PFApp/stores/propilot_lifecycle_store";
import type { DropZone } from "PFComponents/floating_expandable_bubble";
import { useFloatingEntry } from "PFComponents/floating_expandable_bubble/floating_position_store";
import { useCallback, useMemo } from "react";

import { BUBBLE_POSITION_STORAGE_KEY, NAVBAR_DOCK_ZONE_HEIGHT, NAVBAR_DOCK_ZONE_ID } from "../../constants";
import { useDockAndResetPosition } from "../../hooks/use_dock_and_reset_position";

type UseDockZone = {
  dropZones: DropZone[];
  onDragOverZone: (zoneId: string | null) => void;
  onDropInZone: (zoneId: string) => void;
  onDragStateChange: (isDragging: boolean) => void;
};

export const useDockZone = (): UseDockZone => {
  const status = useProPilotStatus();
  const { isOpen: isBoxOpen } = useFloatingEntry(BUBBLE_POSITION_STORAGE_KEY);
  const dockElement = useProPilotDockElement();
  const { setBubbleOverDock } = useProPilotDockActions();
  const { float } = useProPilotLifecycleActions();
  const dockAndResetPosition = useDockAndResetPosition();

  const dropZones = useMemo<DropZone[]>(
    () => [
      {
        id: NAVBAR_DOCK_ZONE_ID,
        getRect: () => {
          const rect = dockElement?.getBoundingClientRect();
          if (!rect) {
            return null;
          }
          const top = rect.bottom - NAVBAR_DOCK_ZONE_HEIGHT;
          return {
            x: rect.left,
            y: top,
            left: rect.left,
            top,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: NAVBAR_DOCK_ZONE_HEIGHT,
            toJSON: () => ({})
          } as DOMRect;
        }
      }
    ],
    [dockElement]
  );

  // Only a collapsed bubble can be docked, so an open panel dragged over the navbar shows no target.
  const onDragOverZone = useCallback(
    (zoneId: string | null) => setBubbleOverDock(!isBoxOpen && zoneId === NAVBAR_DOCK_ZONE_ID),
    [isBoxOpen, setBubbleOverDock]
  );

  const onDragStateChange = useCallback(
    (dragging: boolean) => {
      if (!dragging) {
        setBubbleOverDock(false);
      }
      if (dragging && status === "docked") {
        float();
      }
    },
    [float, setBubbleOverDock, status]
  );

  const onDropInZone = useCallback(
    (zoneId: string) => {
      if (zoneId === NAVBAR_DOCK_ZONE_ID) {
        dockAndResetPosition();
      }
    },
    [dockAndResetPosition]
  );

  return { dropZones, onDragOverZone, onDropInZone, onDragStateChange };
};
